import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const FileCard = ({ file, isSelected, onSelect, onPreview, onContextMenu }) => {
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isImage = file?.type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i?.test(file?.name);
  const isPDF = file?.type === 'application/pdf' || /\.pdf$/i?.test(file?.name);
  const isVideo = file?.type?.startsWith('video/') || /\.(mp4|webm|ogg|mov|avi)$/i?.test(file?.name);
  const isAudio = file?.type?.startsWith('audio/') || /\.(mp3|wav|ogg|m4a|flac)$/i?.test(file?.name);
  const isDocument = /\.(doc|docx|txt|rtf|odt)$/i?.test(file?.name);
  const isSpreadsheet = /\.(xls|xlsx|csv|ods)$/i?.test(file?.name);
  const isPresentation = /\.(ppt|pptx|odp)$/i?.test(file?.name);
  const isArchive = /\.(zip|rar|7z|tar|gz)$/i?.test(file?.name);
  const isCode = /\.(js|jsx|ts|tsx|py|java|cpp|c|cs|php|rb|go|rs)$/i?.test(file?.name);
  const isFolder = file?.isFolder;

  const getFileIcon = () => {
    if (isFolder) return 'Folder';
    if (isImage) return 'Image';
    if (isPDF) return 'FileText';
    if (isVideo) return 'Video';
    if (isAudio) return 'Music';
    if (isDocument) return 'FileText';
    if (isSpreadsheet) return 'Table';
    if (isPresentation) return 'Presentation';
    if (isArchive) return 'Archive';
    if (isCode) return 'Code';
    return 'File';
  };

  const getFileTypeColor = () => {
    if (isFolder) return 'text-yellow-500';
    if (isImage) return 'text-purple-500';
    if (isPDF) return 'text-red-500';
    if (isVideo) return 'text-blue-500';
    if (isAudio) return 'text-green-500';
    if (isDocument) return 'text-blue-600';
    if (isSpreadsheet) return 'text-emerald-500';
    if (isPresentation) return 'text-orange-500';
    if (isArchive) return 'text-gray-500';
    if (isCode) return 'text-cyan-500';
    return 'text-muted-foreground';
  };

  return (
    <div
      className={`file-card ${isSelected ? 'file-card-selected' : ''}`}
      onClick={(e) => {
        if (e?.ctrlKey || e?.metaKey) {
          onSelect(file?.id);
        } else {
          onPreview(file);
        }
      }}
      onContextMenu={(e) => onContextMenu(e, file)}
    >
      <div className="relative w-full aspect-square mb-3 bg-muted rounded-md overflow-hidden">
        {isImage && file?.signedUrl ? (
          <Image
            src={file?.signedUrl}
            alt={file?.alt || `Preview of ${file?.name}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon
              name={getFileIcon()}
              size={48}
              className={getFileTypeColor()}
            />
          </div>
        )}
        
        {!isFolder && (
          <div className="absolute bottom-2 right-2 w-8 h-8 bg-white/90 dark:bg-gray-800/90 rounded-md flex items-center justify-center shadow-sm">
            <Icon
              name={getFileIcon()}
              size={16}
              className={getFileTypeColor()}
            />
          </div>
        )}
        
        {isSelected && (
          <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
            <Icon name="Check" size={16} color="white" />
          </div>
        )}
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground truncate" title={file?.name}>
          {file?.name}
        </p>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="font-mono">{formatFileSize(file?.size)}</span>
          <span>{formatDate(file?.lastModified)}</span>
        </div>
      </div>
    </div>
  );
};

export default FileCard;